import { Link } from '@tanstack/react-router'
import {
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  useSidebar,
} from '@/components/ui/sidebar.tsx'
import { useRecentlyUsedLabels } from '@/routes/features/tags/hooks/useRecentlyUsedLabels'
import { useAllLabels } from '@/lib/hooks/useLabels'
import { cn } from '@/lib/utils'


interface TagsSidebarSectionProps {
  workspaceId: string | null | undefined
  workspaceSlug: string | null | undefined
  /** Normalized current pathname (no trailing slash) */
  currentPath: string
}

const normalizePath = (path: string) => {
  if (!path || path === '/') return path
  return path.replace(/\/+$/, '')
}

export function TagsSidebarSection({
  workspaceId,
  workspaceSlug,
  currentPath,
}: TagsSidebarSectionProps) {
  const { state: sidebarState } = useSidebar()
  const { labels: recent, isLoading: recentLoading } = useRecentlyUsedLabels(
    workspaceId,
    10,
  )
  const { labels: allLabels, isLoading: allLoading } = useAllLabels(workspaceId)

  if (sidebarState === 'collapsed') return null
  if (!workspaceSlug) return null

  // Hide entirely if no labels exist in the workspace at all.
  if (!recentLoading && !allLoading && allLabels.length === 0) return null

  const tagsRootUrl = `/${workspaceSlug}/tags`
  const normalizedTagsRoot = normalizePath(tagsRootUrl)
  const isOnTagsIndex = currentPath === normalizedTagsRoot
  const shouldShowAll = allLabels.length > recent.length

  return (
    <SidebarGroup>
      <SidebarGroupLabel
        asChild
        className="text-xs font-semibold text-muted-foreground uppercase tracking-wider"
      >
        <Link to={tagsRootUrl}>Tags</Link>
      </SidebarGroupLabel>
      <SidebarGroupContent>
        <div className="space-y-0.5">
          {recent.map((label) => {
            const url = `${tagsRootUrl}/${label.id}`
            const isActive = normalizePath(url) === currentPath
            return (
              <Link
                key={label.id}
                to={url}
                className={cn(
                  'group flex items-center gap-2 rounded-none px-4 py-1 text-sm transition-colors',
                  isActive
                    ? 'bg-sidebar-active text-sidebar-active-foreground'
                    : 'text-sidebar-foreground/80 hover:bg-sidebar-accent hover:text-sidebar-accent-foreground',
                )}
              >
                <span className="flex size-4 shrink-0 items-center justify-center">
                  <span
                    aria-hidden
                    className="size-2 rounded-full"
                    style={{ backgroundColor: label.color }}
                  />
                </span>
                <span className="flex-1 min-w-0 truncate">{label.name}</span>
              </Link>
            )
          })}
          {shouldShowAll && (
            <Link
              to={tagsRootUrl}
              className={cn(
                'flex items-center gap-2 rounded-none px-4 py-1 text-sm transition-colors',
                isOnTagsIndex
                  ? 'bg-sidebar-active text-sidebar-active-foreground'
                  : 'text-muted-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground',
              )}
            >
              <span className="size-4 shrink-0" />
              <span className="flex-1 min-w-0 truncate">Show all</span>
            </Link>
          )}
        </div>
      </SidebarGroupContent>
    </SidebarGroup>
  )
}

export default TagsSidebarSection
